import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { appRoutes } from '@/constants'
import { cn } from '@/lib/utils'

export interface FooterLink {
  label: string
  to: string
}

export interface FooterColumn {
  title: string
  links: FooterLink[]
}

export interface FooterProps {
  brand?: ReactNode
  description?: ReactNode
  columns?: FooterColumn[]
  className?: string
}

const defaultColumns: FooterColumn[] = [
  {
    title: 'Marketplace',
    links: [
      { label: 'Cars', to: '/cars' },
      { label: 'Parts', to: '/parts' },
    ],
  },
  {
    title: 'Account',
    links: [
      { label: 'Home', to: appRoutes.home },
      { label: 'Dashboard', to: appRoutes.dashboard.root },
    ],
  },
]

function Footer({ brand, description, columns = defaultColumns, className }: FooterProps) {
  return (
    <footer className={cn('border-t border-border/60 bg-background/40', className)}>
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.5fr_1fr_1fr] lg:px-8">
        <div className="space-y-3">
          {brand}
          {description ? (
            <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {columns.map((column) => (
          <nav key={column.title} aria-label={column.title} className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">{column.title}</h3>
            <ul className="space-y-2">
              {column.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      <div className="border-t border-border/60">
        <div className="mx-auto max-w-7xl px-4 py-5 text-center sm:px-6 lg:px-8">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Dealer Digital Commerce Platform. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

export { Footer }